import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { getCreditProfile } from 'app-components/flux/credit-profile/selectors';
import { getRefreshedCreditReport } from 'app-components/flux/credit-report/actions';
import { rootUiActions } from 'app-components/flux/root-ui';

import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import type { FieldValues } from './CreditReportUpdateModal';
import { CreditReportUpdateProvider, updateCreditReportHooks } from './CreditReportUpdateModal';
import SSNField from './SSNField';

import { CREDIT_REPORT_UPDATE_DIALOG_ID } from './actions';

type typeSSN = {
  ssn: string;
};

const initSSN: typeSSN = {
  ssn: '',
};

type SubmitButtonProps = {
  onClick: (formState: FieldValues) => void;
  disabled?: boolean;
};

const SubmitButton = (props: SubmitButtonProps) => {
  const { onClick, disabled } = props as SubmitButtonProps;
  const formManager = updateCreditReportHooks.useManager();

  return (
    <QButton
      variant='contained'
      onClick={() => formManager.submit(onClick)}
      disabled={disabled}
      id='credit-score-refresh-continue-btn'
    >
      Continue
    </QButton>
  );
};

const CreditReportUpdateDialog: React.FC = () => {
  const dispatch = useDispatch();
  const creditProfileData = useSelector(getCreditProfile);

  const handleDialogClose = () => {
    dispatch(rootUiActions.removeDialog(CREDIT_REPORT_UPDATE_DIALOG_ID));
  };

  const handleContinueClick = ({ ssn }: FieldValues) => {
    if (creditProfileData?.id) {
      dispatch(
        getRefreshedCreditReport({
          creditProfileRegistrationId: creditProfileData?.id,
          ssn: ssn.replaceAll?.('-', ''),
        }),
      );
    }
    handleDialogClose();
  };

  return (
    <CreditReportUpdateProvider input={initSSN}>
      <StdDialog
        open
        title='Verify your SSN'
        onClose={handleDialogClose}
        paperProps={{
          sx: {
            maxWidth: 'fit-content',
            width: 737,
            height: 422,
          },
        }}
      >
        <DialogContent sx={{ height: 282 }}>
          <Typography variant='headlineMedium'>Enter your Social Security Number to view your Credit Score.</Typography>
          <Typography variant='paragraphRegular'>Viewing your report does not impact your credit score.</Typography>
          <Grid container mt={6}>
            <SSNField isUpdating />
          </Grid>
        </DialogContent>
        <DialogActions>
          <SubmitButton onClick={handleContinueClick} />
        </DialogActions>
      </StdDialog>
    </CreditReportUpdateProvider>
  );
};

export default CreditReportUpdateDialog;
